/**
 * arloIdentity.js - Who is this viewer, as far as Arlo is concerned?
 *
 * Arlo's customer_id is the active *profile* id, NOT the account id. Taste is a
 * property of a viewer, not a household: every taste signal ChillFree collects
 * (reactions, watchlist, resumptions) is already keyed on the current profile,
 * so the id we send to Arlo must be too — otherwise a kid's profile and an
 * adult's profile on one account blend into a single Arlo taste profile and
 * cross-contaminate each other's recommendations.
 *
 * `profile._id` is opaque to Arlo (a Mongo id; Arlo never sees who it belongs
 * to — ChillFree holds that mapping). A future step may swap this for a
 * dedicated per-profile anon id minted server-side in Spotlight, so Arlo never
 * receives a ChillFree-canonical id at all; the id source is centralized here
 * so that swap is a one-line change.
 *
 * Signed-in, profile-selected visitors only. resolveCustomerId() returns null
 * when there is no active profile (signed out, or signed in but on the
 * profile-picker), and every Arlo surface that needs an identity is gated on
 * it: rails and chat are profile-scoped features, and the PDP "More Like This"
 * rail calls similar() with no customerId (never personalized — same result for
 * everyone — so a profile-less view just isn't attributed).
 *
 * There is deliberately no anonymous-id fallback. Minting a stable per-browser
 * id would build a durable taste profile for someone who never made an account
 * and never got asked, which is both a disclosure burden and a contradiction of
 * the consent banner (src/consent/) — that gate promises trackers don't load
 * until you accept, and a persistent Arlo id written past a "Reject" click
 * would make it a lie. If anonymous personalization ever comes back it needs a
 * consent check and a privacy-policy section, not a UUID here.
 *
 * NOT YET HANDLED — kids' profiles. Building a behavioral profile on a child is
 * a COPPA exposure, but ChillFree's profile model carries no kid/maturity flag
 * today, so this file cannot tell a kid profile apart. When Spotlight adds that
 * flag, gate it here (kid profile → return null → generic rails, no id sent).
 */
import profileService from '../services/ProfileService';

export const resolveCustomerId = () => {
  return profileService.getCurrentProfile()?._id || null;
};

/**
 * Legacy anonymous ids are no longer minted, but browsers that visited while
 * the old build was live still hold one. Clear it so a stale id can't be picked
 * up by anything and so the key doesn't linger in storage we no longer document.
 * Cheap, idempotent, safe to call on every load.
 */
export const purgeLegacyAnonymousId = () => {
  try {
    localStorage.removeItem('arlo_anonymous_id');
  } catch (e) {
    /* localStorage unavailable — nothing to purge */
  }
};
